/**
 * The monthly ledger's derived view (P4-T2).
 *
 * Everything the UI shows as *money* is computed here, and every figure is computed by
 * `@saldo/domain` — `openingSum`, `activitySum`, `closingBalance`, `balanceAt`,
 * `isActualEligible`, `ordinaryReport`. There is no second eligibility or sign rule in the
 * UI: the same rule that decides whether a row moves money decides what the balances are,
 * which is the only way "row filters never change money" can be true rather than merely
 * asserted.
 *
 * The other half of that claim lives in the shape of this module. A summary is computed
 * over **all** loaded entries; the row list is a *separate* computation over the same
 * entries. Filtering, searching, sorting and paging change only the second one, so they
 * cannot move a figure — not because the code is careful, but because the summary never
 * sees the filtered list.
 *
 * History that was not loaded is labelled, never summed as if it were everything. The wire
 * list read is bounded (`limit`, the API's 200 maximum), and it is ordered oldest-first, so
 * the loaded rows are a *prefix* of the account's history: a running sum over them is exact
 * for the rows that are there, while any window that reaches past the last loaded date is
 * genuinely unknown and says so.
 */

import {
  balanceAt,
  closingBalance,
  isActualEligible,
  ordinaryReport,
  openingSum,
  type AccountKind,
  type LedgerEntry,
} from '@saldo/domain';
import type { AccountBody, EntryBody } from '@saldo/contracts';

/** The wire list read's bound; the API's own maximum (`entryListQuerySchema`). */
export const ENTRY_PAGE_LIMIT = 200;

/** How many rows a page of the ledger shows. */
export const ROWS_PER_PAGE = 25;

export interface LoadedHistory {
  /** True when the whole account history is loaded (fewer rows than the bound). */
  readonly complete: boolean;
  /** The latest business date among the loaded rows, or `null` when there are none. */
  readonly through: string | null;
  readonly count: number;
}

/**
 * Project a wire entry onto the domain's entry shape.
 *
 * The wire's `effectiveDate` is the row's own business date (`due_date`, or `opening_date`
 * on an opening); the domain keeps those two apart, so the projection puts it back where
 * `effectiveDate()` looks for it. Nothing else is derived: `signedMinor` stays exact, and
 * `canceled` is the row's own marker — the list read returns canceled rows only when asked
 * (`includeCanceled`), and when it does, the one eligibility rule is what makes them contribute
 * zero rather than the client assuming none of them is there (P7-T1).
 */
export function toDomainEntry(entry: EntryBody, accountKind: AccountKind): LedgerEntry {
  const opening = entry.type === 'opening';
  const transfer = entry.type === 'transfer';
  return {
    type: entry.type,
    signedMinor: BigInt(entry.signedMinor),
    accountKind,
    dueDate: opening || transfer ? null : entry.effectiveDate,
    openingDate: opening ? entry.effectiveDate : null,
    paid: entry.paid,
    canceled: entry.canceled,
    // A transfer leg carries no date and no paid flag of its own: its business date is the
    // parent's shared date, which the wire reports as this row's `effectiveDate`, and it moves
    // money only while its pair is completed. Both have to be put back here, or a completed
    // payment is a row that the domain sees as timeless and ineligible — which is exactly how
    // the month-end figure and the row running sums came to ignore every payment (P6-T3).
    ...(transfer
      ? {
          transferDate: entry.effectiveDate,
          transferCompleted: entry.transferStatus === 'completed',
        }
      : {}),
  };
}

export function loadedHistory(entries: readonly EntryBody[], limit = ENTRY_PAGE_LIMIT): LoadedHistory {
  let through: string | null = null;
  for (const entry of entries) {
    if (through === null || entry.effectiveDate > through) through = entry.effectiveDate;
  }
  return { complete: entries.length < limit, through, count: entries.length };
}

/**
 * Whether a figure whose window ends at `cutoff` (inclusive) is exact, given the loaded
 * prefix.
 *
 * A complete history is exact everywhere. A truncated one is exact up to its last loaded
 * date and unknown past it — which is the difference between "the month-end balance is
 * 0.00" and "the month-end balance is not known from what is loaded".
 */
export function isExactAt(history: LoadedHistory, cutoff: string): boolean {
  if (history.complete) return true;
  return history.through !== null && cutoff <= history.through;
}

export interface LedgerSummary {
  readonly currency: string;
  /** The server's balance at `businessToday`, over the account's whole history. */
  readonly currentMinor: bigint;
  readonly currentNegative: boolean;
  /** Opening before the month starts; `null` when the loaded prefix cannot establish it. */
  readonly openingMinor: bigint | null;
  /** Recorded balance through the month's end; `null` when it cannot be established. */
  readonly monthEndMinor: bigint | null;
  readonly monthLabel: string;
  readonly history: LoadedHistory;
  /** Why a `null` figure is unknown, or `null` when both figures are exact. */
  readonly unknownReason: string | null;
}

export interface SummaryInput {
  readonly account: AccountBody;
  readonly entries: readonly EntryBody[];
  readonly monthStart: string;
  readonly monthEndExclusive: string;
  /** The server's business day, from the envelope's `meta`. */
  readonly businessToday: string;
  readonly history: LoadedHistory;
}

/**
 * The summary panel's figures.
 *
 * `current` is the API's own derived balance at the business day — the one figure that
 * includes history the client may not have loaded — and the month figures are the domain's
 * arithmetic over the loaded prefix, each one either exact or explicitly unknown.
 */
export function summarise(input: SummaryInput): LedgerSummary {
  const { account, entries, history } = input;
  const kind = account.kind as AccountKind;
  const rows = entries.map((entry) => toDomainEntry(entry, kind));

  // A window ending on the month's last day is `endExclusive` minus one day; the domain's
  // half-open periods make that the same computation `closingBalance` performs.
  const monthEnd = previousDay(input.monthEndExclusive);
  const openingExact = isExactAt(history, previousDay(input.monthStart));
  const closingExact = isExactAt(history, monthEnd);
  const unknownReason = unknownReasonFor(history, openingExact, closingExact);

  return {
    currency: account.currency,
    currentMinor: BigInt(account.balance.minorUnits),
    currentNegative: BigInt(account.balance.minorUnits) < 0n,
    openingMinor: openingExact ? openingSum(rows, input.monthStart) : null,
    monthEndMinor: closingExact
      ? closingBalance(rows, input.monthStart, input.monthEndExclusive)
      : null,
    monthLabel: input.monthStart.slice(0, 7),
    history,
    unknownReason,
  };
}

/**
 * Why a figure is unknown, in words — `null` when both are exact.
 *
 * A truncated read is not automatically a broken view: because the list is ordered
 * oldest-first the loaded rows are a prefix, so a month inside that prefix is still exact.
 * Only the part past the last loaded date is genuinely unknown, and this is where that is
 * said rather than left as a bare `0`.
 */
function unknownReasonFor(
  history: LoadedHistory,
  openingExact: boolean,
  closingExact: boolean,
): string | null {
  if (history.complete) return null;
  const loaded = `only the ${history.count} entries through ${history.through ?? '—'} are loaded`;
  if (openingExact && closingExact) {
    return `Newer history is not loaded — ${loaded}; this month is exact, later months are not.`;
  }
  const which =
    !openingExact && !closingExact
      ? 'the opening and month-end figures are'
      : !openingExact
        ? 'the opening figure is'
        : 'the month-end figure is';
  return `Newer history is not loaded — ${loaded}, so ${which} not known from them.`;
}

/** The day before a business date, by calendar arithmetic (never a `Date`). */
export function previousDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number) as [number, number, number];
  if (day > 1) return `${pad(year)}-${pad(month)}-${pad(day - 1)}`;
  const previousMonth = month === 1 ? 12 : month - 1;
  const previousYear = month === 1 ? year - 1 : year;
  return `${pad(previousYear)}-${pad(previousMonth)}-${pad(daysInMonth(previousYear, previousMonth))}`;
}

function pad(value: number): string {
  return String(value).padStart(2, '0');
}

function daysInMonth(year: number, month: number): number {
  if (month === 2) return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
  return [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1] ?? 0;
}

export interface LedgerRow {
  readonly entry: EntryBody;
  /** The signed sum of every eligible loaded row up to and including this one. */
  readonly runningMinor: bigint;
  /** Whether this row moves money under the one eligibility rule (`isActualEligible`). */
  readonly eligible: boolean;
  /** The row's own business date. */
  readonly date: string;
  readonly transactionDate: string | null;
  readonly stateLabel: string;
}

/**
 * Every loaded row, in chronological order, with its running sum.
 *
 * The running sum is `balanceAt` over the loaded prefix, which is exact for the prefix
 * (`loadedHistory`); it is a property of the account's chronology, not of whatever filter
 * the list is currently showing.
 */
export function chronologicalRows(
  entries: readonly EntryBody[],
  accountKind: AccountKind,
  businessToday: string,
): LedgerRow[] {
  const ordered = [...entries].sort((left, right) =>
    left.effectiveDate === right.effectiveDate
      ? left.id.localeCompare(right.id)
      : left.effectiveDate.localeCompare(right.effectiveDate),
  );
  const domain = ordered.map((entry) => toDomainEntry(entry, accountKind));
  return ordered.map((entry, index) => ({
    entry,
    date: entry.effectiveDate,
    transactionDate: entry.transactionDate,
    eligible: isActualEligible(domain[index] as LedgerEntry),
    runningMinor: balanceAt(domain.slice(0, index + 1), entry.effectiveDate),
    stateLabel: rowStateLabel(entry, accountKind, businessToday, domain[index] as LedgerEntry),
  }));
}

/** The state words the row shows; the same vocabulary the prototype uses. */
export function rowStateLabel(
  entry: EntryBody,
  accountKind: AccountKind,
  businessToday: string,
  domainEntry: LedgerEntry,
): string {
  // A canceled row is shown only when it is asked for, and it is its own state: it is neither
  // pending nor effective, because it contributes to nothing (`UX-FLOWS.md` §7).
  if (entry.canceled) return 'Canceled';
  if (entry.type === 'opening') return 'Adjustment';
  if (entry.type === 'transfer') return 'Transfer';
  const eligible = isActualEligible(domainEntry);
  const past = entry.effectiveDate <= businessToday;
  if (accountKind === 'card') return past ? 'Effective' : 'Future';
  if (eligible) return past ? 'Paid / received' : 'Paid · future';
  return past ? 'Pending · overdue' : 'Pending';
}

/** How a row's date is matched against the period. */
export type RowMode = 'due' | 'transaction';

/** The date a row is placed by in the selected mode. */
export function membershipDate(row: LedgerRow, mode: RowMode): string {
  return mode === 'due' ? row.date : (row.transactionDate ?? row.date);
}

export interface RowQuery {
  readonly mode: RowMode;
  readonly query: string;
  readonly type: 'all' | 'income' | 'expense' | 'opening' | 'transfer';
  readonly state: 'all' | 'effective' | 'pending';
  readonly sort: 'date-asc' | 'date-desc' | 'amount-asc' | 'amount-desc';
  readonly monthStart: string;
  readonly monthEndExclusive: string;
  readonly businessToday: string;
  readonly page: number;
}

export interface RowSelection {
  /** Every row that matches the period and the secondary filters, sorted. */
  readonly matching: LedgerRow[];
  /** The page of `matching` the view shows. */
  readonly pageRows: LedgerRow[];
  readonly page: number;
  readonly pageCount: number;
  readonly total: number;
}

/**
 * Select the rows the list shows — and *only* the rows.
 *
 * The period membership, the search, the type/state filters, the sort and the page are all
 * here; no money figure is. That is deliberate and structural: the summary cannot be
 * affected by a filter because it is not computed from this function's output.
 */
export function selectRows(rows: readonly LedgerRow[], query: RowQuery): RowSelection {
  const needle = query.query.trim().toLowerCase();
  const matching = rows
    .filter((row) => {
      const date = membershipDate(row, query.mode);
      if (date < query.monthStart || date >= query.monthEndExclusive) return false;
      if (query.type !== 'all' && row.entry.type !== query.type) return false;
      if (query.state !== 'all') {
        // A canceled row is its own state: it is neither effective nor pending, so a state filter
        // never selects it — it is only reachable with no state filter and the canceled toggle on.
        if (row.entry.canceled) return false;
        const effective = row.eligible && row.date <= query.businessToday;
        if (query.state === 'effective' ? !effective : effective) return false;
      }
      if (needle === '') return true;
      return row.entry.description.toLowerCase().includes(needle);
    })
    .sort((left, right) => compareRows(left, right, query.sort));

  const pageCount = Math.max(1, Math.ceil(matching.length / ROWS_PER_PAGE));
  const page = Math.min(Math.max(1, query.page), pageCount);
  const pageRows = matching.slice((page - 1) * ROWS_PER_PAGE, page * ROWS_PER_PAGE);
  return { matching, pageRows, page, pageCount, total: matching.length };
}

function compareRows(left: LedgerRow, right: LedgerRow, sort: RowQuery['sort']): number {
  const byDate =
    left.date === right.date ? left.entry.id.localeCompare(right.entry.id) : left.date.localeCompare(right.date);
  if (sort === 'date-asc') return byDate;
  if (sort === 'date-desc') return -byDate;
  const leftMinor = BigInt(left.entry.signedMinor);
  const rightMinor = BigInt(right.entry.signedMinor);
  if (leftMinor === rightMinor) return byDate;
  const ascending = leftMinor < rightMinor ? -1 : 1;
  return sort === 'amount-asc' ? ascending : -ascending;
}

/**
 * The activity subtotal for the rows currently shown.
 *
 * It is the domain's `ordinaryReport` over the matching rows — so it also never counts an
 * opening as income — and the caller labels it as filtered whenever a filter is active,
 * because a subtotal over narrowed rows is not a balance.
 */
export function matchingActivity(
  selection: RowSelection,
  accountKind: AccountKind,
  start: string,
  endExclusive: string,
): { readonly incomeMinor: bigint; readonly expenseMinor: bigint; readonly netMinor: bigint } {
  const domain = selection.matching.map((row) => toDomainEntry(row.entry, accountKind));
  return ordinaryReport(domain, start, endExclusive);
}

export interface CurrencyGroup {
  readonly currency: string;
  readonly cashMinor: bigint;
  readonly cardMinor: bigint;
}

/**
 * The word a card figure carries, from the sign of that figure alone (`CARD-03`).
 *
 * A card balance is a signed sum: negative is what you owe, positive is credit in your
 * favour, and zero is neither. The word is derived here — once — from the same number the
 * screen prints, so the label cannot disagree with the sign, and the positive case is
 * never presented as money owed (`UX-FLOWS.md` §2).
 */
export type CardState = 'debt' | 'credit' | 'settled';

export function cardState(minor: bigint): CardState {
  if (minor < 0n) return 'debt';
  if (minor > 0n) return 'credit';
  return 'settled';
}

/**
 * The directory's per-currency totals.
 *
 * There is no combined figure across currencies and this type has nowhere to put one: BRL,
 * CAD and USD are kept separate, exactly as `UX-FLOWS.md` §2 requires, so no viewer can
 * read a converted grand total that does not exist.
 */
export function byCurrency(accounts: readonly AccountBody[]): CurrencyGroup[] {
  const groups = new Map<string, { cashMinor: bigint; cardMinor: bigint }>();
  for (const account of accounts) {
    const group = groups.get(account.currency) ?? { cashMinor: 0n, cardMinor: 0n };
    const minor = BigInt(account.balance.minorUnits);
    if (account.kind === 'card') group.cardMinor += minor;
    else group.cashMinor += minor;
    groups.set(account.currency, group);
  }
  return [...groups.entries()]
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([currency, totals]) => ({ currency, ...totals }));
}
